/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useLazyQuery } from '@apollo/client';
import { Box, Button, Flex, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import moment from 'moment';
import 'moment/locale/en-gb';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import c from '../../constant';
import useStore from '../../store';
import CheckBoxFormField from '../form/CheckBoxFormField';
import CompanyFormField from '../form/Company/CompanyFormField';
import CompanyTypeFormField from '../form/Company/CompanyTypeFormField';
import DateRangeFormField from '../form/DateRangeFormField';
import RadioFormField from '../form/RadioFormField';

const QUERY_COMPANY_REPORT_TYPE = gql`
	query get($period: String!) {
		getPublicCompanyReportTypes(period: $period) {
			id
			title
			titleKh
		}
	}
`;

const filterSchema = Yup.object().shape({
	companyType: Yup.string().nullable(),
	company: Yup.object().nullable(),
	createdDate: Yup.string(),
	unreadOnly: Yup.boolean(),
	showSpam: Yup.boolean(),
	newReportType: Yup.string(),
});

function ComplaintFilter({
	isOpen = false,
	onClose = () => null,
	onCallback = () => null,
	companyType = null,
	company = null,
	fromDate = null,
	toDate = null,
	read = null,
	title = null,
	titleKh = null,
	type = '',
	showSpam = null,
	newReportType = '',
}) {
	moment.locale('en-gb');
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const [loadReportTypeByPeriod, { data: dataReportType }] = useLazyQuery(QUERY_COMPANY_REPORT_TYPE);
	const [tempNewReportType, setTempNewReportType] = useState(newReportType);

	useEffect(() => isOpen && type && loadReportTypeByPeriod({ variables: { period: type } }), [isOpen, type]);

	let defaultRange = '';
	if (fromDate && toDate) {
		const defaultFrom = JSON.parse(fromDate);

		const defaultTo = JSON.parse(toDate);
		defaultRange = {
			from: defaultFrom,
			to: defaultTo,
		};
	}

	function getTypeData(data) {
		let res = [];
		data?.forEach((item) => {
			res.push({
				label: lang === 'kh' ? item?.titleKh : item?.title,
				value: item?.id,
			});
		});
		return res;
	}

	return (
		<>
			<Modal borderRadius={c.borderRadius} isOpen={isOpen} onClose={onClose}>
				<ModalOverlay />
				<ModalContent borderRadius={c.borderRadius}>
					<ModalHeader>{t('Filter')}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Formik
							initialValues={{
								companyType,
								company,
								createdDate: defaultRange ? JSON.stringify(defaultRange) : '',
								unreadOnly: read ? read : '',
								showSpam: showSpam ? showSpam : false,
								newReportType: newReportType ? newReportType : '',
							}}
							validationSchema={filterSchema}
							onSubmit={(values) => {
								let url = `?type=${type?.toLowerCase()}&page=1`;
								if (values?.companyType) {
									url += `&companyType=${values?.companyType}`;
								}
								if (values?.company?.companyId) {
									url = url + `&companyId=${values?.company?.companyId}`;
								}
								if (values?.company?.companyName) {
									url = url + `&companyName=${values?.company?.companyName}`;
								}
								if (values?.createdDate) {
									const tmpDate = JSON.parse(values.createdDate);
									if (tmpDate?.from && tmpDate?.to) {
										const dateFrom = JSON.stringify(tmpDate.from);
										const dateTo = JSON.stringify(tmpDate.to);
										url += `&fromDate=${dateFrom}&toDate=${dateTo}`;
									}
								}
								if (values?.newReportType) {
									url += `&newReportType=${tempNewReportType}`;
								}
								if (values?.unreadOnly) {
									url += `&read=false`;
								}
								if (values.showSpam) {
									url += `&showSpam=true`;
								}
								if (title) {
									url += `&title=${encodeURIComponent(title)}`;
								}
								if (titleKh) {
									url += `&titleKh=${encodeURIComponent(titleKh)}`;
								}
								onCallback(url);
								onClose();
							}}
						>
							{(props) => (
								<Box pb="4">
									<Form>
										<CompanyTypeFormField
											{...props}
											name="companyType"
											companyFormName="company"
											label="Company Type"
											placeholder={t('Company Type')}
											types={c.insuranceCompanyTypes}
										/>
										<CompanyFormField
											{...props}
											name="company"
											companyTypeFormName="companyType"
											label="Company"
											types={c.insuranceCompanyTypes.map((e) => e?.value)}
										/>
										<center>
											<DateRangeFormField {...props} label="Created Date" name="createdDate" initialDate={{ fromDate, toDate }} />
										</center>
										<Box display="none">
											{dataReportType?.getPublicCompanyReportTypes?.length > 0 && (
												<RadioFormField
													label="Report Type"
													name="newReportType"
													radios={getTypeData(dataReportType?.getPublicCompanyReportTypes)}
													initValue={newReportType}
													callbackValueChange={(e) => setTempNewReportType(e)}
												/>
											)}
										</Box>
										<Box marginTop="4">
											<CheckBoxFormField {...props} label="Show unread only" name="unreadOnly" />
										</Box>
										<Box marginTop="4">
											<CheckBoxFormField {...props} label="Show spam" name="showSpam" />
										</Box>
										<Flex mt="32px" justifyContent="flex-end">
											<Button
												id="login-btn"
												variant="solid"
												size="md"
												h="42px"
												w="128px"
												type="submit"
												borderRadius={c.borderRadius}
												color="#3498db"
											>
												{t('Apply')}
											</Button>
											<Button
												size="md"
												h="42px"
												ml={4}
												w="128px"
												onClick={() => {
													let clearUrl = `?type=${type?.toLowerCase()}&page=1`;

													if (title) clearUrl += `&title=${encodeURIComponent(title)}`;
													if (titleKh) clearUrl += `&titleKh=${encodeURIComponent(titleKh)}`;
													if (newReportType) clearUrl += `&newReportType=${newReportType}`;

													onCallback(clearUrl);
													onClose();
												}}
												variant="ghost"
											>
												{t('Clear')}
											</Button>
										</Flex>
									</Form>
								</Box>
							)}
						</Formik>
					</ModalBody>
				</ModalContent>
			</Modal>
		</>
	);
}

export default ComplaintFilter;
